import { nightHawkPlatform, sensorEase } from './night-hawk-platform';
import { nightHawkOptics } from './night-hawk-optics';

export interface NightHawkEffects {
  slew: number;
  hunt: number;
  tilt: number;
  /** Total platform rotation over the pass, in degrees from 0 through 360. */
  turn: number;
  climb: number;
  tempo: number;
  targetLock: boolean;
}

export const NIGHT_HAWK_EFFECT_DEFAULTS: Readonly<NightHawkEffects> = { slew: 0, hunt: 0, tilt: 0, turn: 0, climb: 0, tempo: 1, targetLock: false };
export const NIGHT_HAWK_EFFECT_PROFILES = [
  { id: 'locked', label: 'Locked', effects: { ...NIGHT_HAWK_EFFECT_DEFAULTS } },
  { id: 'hunt', label: 'Gentle Hunt', effects: { ...NIGHT_HAWK_EFFECT_DEFAULTS, hunt: 1 } },
  { id: 'slew', label: 'Operator Slew', effects: { ...NIGHT_HAWK_EFFECT_DEFAULTS, slew: 1 } },
  { id: 'receding', label: 'Receding', effects: { ...NIGHT_HAWK_EFFECT_DEFAULTS, tilt: 1.1 } },
  { id: 'turning-climb', label: 'Turning Climb', effects: { ...NIGHT_HAWK_EFFECT_DEFAULTS, tilt: .9, turn: 42, climb: .65 } },
  { id: 'cinematic', label: 'Combined', effects: { slew: .7, hunt: .55, tilt: .85, turn: 27.3, climb: .35, tempo: 1, targetLock: false } },
] as const;

/** Validate controls before compiling a plan or updating the sensor presentation. */
export function resolveNightHawkEffects(value?: Partial<NightHawkEffects>): NightHawkEffects {
  const result = { ...NIGHT_HAWK_EFFECT_DEFAULTS, ...value };
  if (typeof result.targetLock !== 'boolean') throw new RangeError('Night Hawk Target Lock must be a boolean.');
  for (const [key, number] of Object.entries(result)) {
    if (key === 'targetLock') continue;
    if (typeof number !== 'number') throw new RangeError(`Night Hawk ${key} must be numeric.`);
    const minimum = key === 'tempo' ? .5 : key === 'climb' ? -1 : 0;
    const maximum = key === 'turn' ? 360 : key === 'climb' ? 1 : 2;
    if (!Number.isFinite(number) || number < minimum || number > maximum) throw new RangeError(`Night Hawk ${key} is outside its supported range.`);
  }
  return result;
}

/** Continuous aim drift and operator catch-up, evaluated independently at any film time. */
export function nightHawkDisturbance(seconds: number, effects: NightHawkEffects) {
  const time = seconds * effects.tempo;
  const envelope = sensorEase((time - 6) / 4) * (1 - sensorEase((time - 10) / 9));
  const precise = effects.targetLock;
  const lagSeconds = precise ? 0 : 1.8 * effects.slew * envelope;
  const fade = sensorEase(time / 5);
  const hunt = precise ? 0 : effects.hunt;
  const huntX = hunt * fade * (.009 * Math.sin(time * .75) + .004 * Math.sin(time * 1.11 + .4));
  const huntY = hunt * fade * (.008 * Math.sin(time * .61 + 1) + .003 * Math.sin(time * 1.03));
  const platform = nightHawkPlatform(seconds, effects);
  const optics = nightHawkOptics(platform.slantRangeFactor, precise);
  return {
    ...platform,
    ...optics,
    zoom: optics.zoomOffset,
    roll: 0,
    lagSeconds,
    aimX: huntX,
    aimY: huntY,
    bracketX: huntX * .35,
    bracketY: huntY * .35,
    state: lagSeconds > .12 ? 'SLEW' : Math.hypot(huntX, huntY) > .002 ? 'DRIFT'
      : platform.travel > .005 && (effects.tilt || effects.turn || effects.climb) ? 'MANEUVER' : 'LOCK',
  };
}
